import React, { useRef, useEffect, useState, useLayoutEffect, useContext } from 'react'
import { FormFieldLabel } from 'components/spreadsheet/components/form/fieldLabel'
import { IColumnTypes, rowHeights } from 'components/spreadsheet/types'
import {
  getRowHeightClassName,
  getRowMinHeightClassName,
  getRowHeightVariable
} from 'components/spreadsheet/helpers/functions'
import { cellStyles } from 'components/spreadsheet/components/cell/styles'
import { HEADER_HEIGHT } from 'style/constants'

interface TextCellProps {
  value: string
  align: 'center' | 'left' | 'right'
  color: string
  kind: IColumnTypes
  readOnly: boolean
  editing: boolean
  rowHeight: number
  selected: boolean
  isExpanded?: boolean
  validation: boolean
  handleKeyDown: (event: React.KeyboardEvent) => void
  setCellValue: (value: string | null) => void
}

const styles = cellStyles()

// The editor opens over its cell, sized to its text, but the grid's sticky header and
// aggregates bar paint over it and the grid doesn't scroll to reveal it. So keep it in
// the space between them: no taller than that space (the text scrolls inside), and moved
// up out of the bar's way only as far as the header. Under a short table, e.g. one
// filtered to a single row, that space is small, and moving up without the limit hid the
// editor under the header.
const fitTextareaInGrid = (textarea: HTMLTextAreaElement) => {
  const grid = textarea.closest('[data-testid="data-view-spreadsheet"]')
  if (!grid) return

  // Measured from the editor's own place and minimum each time, as it's fitted again when the
  // grid resizes; where its text is scrolled to is kept.
  const { scrollTop } = textarea
  textarea.style.marginTop = ''
  textarea.style.minHeight = ''

  const gridRect = grid.getBoundingClientRect()
  const footer = grid.querySelector('#footer')
  const top = gridRect.top + HEADER_HEIGHT
  // Without the bar, the grid's inside edge: above its horizontal scrollbar, where scrollbars take up room
  const bottom = footer ? footer.getBoundingClientRect().top : gridRect.top + grid.clientTop + grid.clientHeight

  // max-height leaves out the padding and border on a content-box textarea
  const computed = getComputedStyle(textarea)
  const chrome = computed.boxSizing === 'content-box' ? textarea.offsetHeight - parseFloat(computed.height) : 0
  const maxHeight = Math.max(bottom - top - chrome, 0)
  textarea.style.maxHeight = `${maxHeight}px`
  // the row-height minimum would otherwise win over it under a one-row table
  textarea.style.minHeight = `${Math.min(parseFloat(computed.minHeight) || 0, maxHeight)}px`

  const rect = textarea.getBoundingClientRect()
  const shift = Math.min(Math.max(rect.bottom - bottom, 0), Math.max(rect.top - top, 0))
  textarea.style.marginTop = shift > 0 ? `-${shift}px` : ''
  textarea.scrollTop = scrollTop
}

const TextCellComponent: React.FC<TextCellProps> = ({
  value,
  align,
  color,
  kind,
  readOnly,
  editing,
  rowHeight,
  selected,
  isExpanded,
  validation,
  handleKeyDown,
  setCellValue
}) => {
  // In a form, named as the field it's drawn in.
  const labelledBy = useContext(FormFieldLabel)
  const inputRef = useRef<HTMLInputElement>(null)
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const [currentValue, setCurrentValue] = useState<string>(value ? value : '')
  const [cursorPosition, setCurrentPosition] = useState<number>()

  useEffect(() => {
    const newValue = value ? value : ''
    if (newValue !== currentValue) {
      setCurrentValue(newValue)
    }
  }, [value])

  // Autofocus when opening on form view
  useEffect(() => {
    if (!editing) return

    const frameId = requestAnimationFrame(() => {
      if (textareaRef.current) {
        textareaRef.current.focus()
      }
    })
    return () => cancelAnimationFrame(frameId)
  }, [editing])

  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.focus()
      if (cursorPosition !== undefined) {
        textareaRef.current.setSelectionRange(cursorPosition, cursorPosition)
      }
    }
  }, [currentValue])

  // We use useLayoutEffect here, because we need to wait for all DOM event to finish before
  // focusing the input, and setting the cursor to the end of the text
  useLayoutEffect(() => {
    if (editing && kind === 'text' && textareaRef.current) {
      textareaRef.current.style.height = textareaRef.current.scrollHeight + 'px'
      fitTextareaInGrid(textareaRef.current)
      textareaRef.current.focus()
      // move cursor to end of text
      const position = value ? value.length : 0
      textareaRef.current.setSelectionRange(position, position)
      // and show it there, when the text scrolls inside
      textareaRef.current.scrollTop = textareaRef.current.scrollHeight
    }

    if (editing && inputRef.current) {
      inputRef.current.focus()
    }
  }, [editing])

  // Fit it again when its size changes (its corner is dragged), when the grid's does (the window, full
  // screen), and when the grid moves its row, as it does to keep the row in view once it has resized.
  useEffect(() => {
    const textarea = textareaRef.current
    const grid = textarea?.closest('[data-testid="data-view-spreadsheet"]')
    if (!editing || kind !== 'text' || !textarea || !grid) return

    const fit = () => fitTextareaInGrid(textarea)
    const resizeObserver = new ResizeObserver(fit)
    resizeObserver.observe(grid)
    resizeObserver.observe(textarea)
    // The row moves by the styles of the cell, the row and the rows layer between the editor and the grid
    const moveObserver = new MutationObserver(fit)
    for (let element = textarea.parentElement; element && element !== grid; element = element.parentElement) {
      moveObserver.observe(element, { attributes: true, attributeFilter: ['style'] })
    }
    return () => {
      resizeObserver.disconnect()
      moveObserver.disconnect()
    }
  }, [editing, kind])

  const handleKeyDownEditingTextArea = (event: React.KeyboardEvent) => {
    const isSpecialKeyPressed = event.ctrlKey || event.metaKey || event.altKey
    if (event.key === 'Enter' && isSpecialKeyPressed) {
      // Add new line if user presses Ctrl, Alt, or Meta keys + Enter
      if (textareaRef.current) {
        const textRefValue = textareaRef.current.value
        const curPos = textareaRef.current.selectionStart
        let valueWithLineBreak = textRefValue
        if (curPos === textRefValue.length) {
          // Add break line at the end of the text
          valueWithLineBreak = textRefValue + String.fromCharCode(13, 10)
          setCurrentPosition(undefined)
        } else {
          // Add break line in the middle of the text
          valueWithLineBreak = textRefValue.slice(0, curPos) + String.fromCharCode(13, 10) + textRefValue.slice(curPos)
          setCurrentPosition(curPos + 1)
        }

        setCellValue(valueWithLineBreak)
      }
    } else {
      setCurrentPosition(undefined)
      handleKeyDown(event)
    }
  }

  if (editing && !readOnly) {
    switch (kind) {
      case 'text':
        // transition-colors: only its colours ease (all of a textarea's styles do by default), so it's
        // fitted in the grid at once, and measured where it is when it's fitted again
        return (
          <textarea
            aria-labelledby={labelledBy}
            ref={textareaRef}
            className={styles.editing({
              focused: editing,
              class: `cell-value-editing pt-[10px] font-inherit transition-colors ${isExpanded ? 'resize-y box-border' : 'resize box-content'} ${getRowMinHeightClassName(rowHeight)}`
            })}
            data-focussed={editing || undefined}
            onChange={(event: React.ChangeEvent<HTMLTextAreaElement>) => {
              setCurrentValue(event.target.value)
              setCellValue(event.target.value)
            }}
            value={currentValue ?? ''}
            style={{ minWidth: 'var(--cell-width)' }}
            onKeyDown={handleKeyDownEditingTextArea}
          ></textarea>
        )
      default:
        return (
          <input
            aria-labelledby={labelledBy}
            className={styles.editing({
              focused: true,
              class: `cell-value-editing ${getRowHeightClassName(rowHeight)}`
            })}
            data-focussed
            value={currentValue ?? ''}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
              setCurrentValue(event.target.value)
              setCellValue(event.target.value)
            }}
            onKeyDown={handleKeyDown}
            ref={inputRef}
          ></input>
        )
    }
  } else {
    return (
      <div
        className={styles.value({ focused: selected, class: 'cell-value' })}
        data-focussed={selected || undefined}
        tabIndex={-1}
        style={{
          textAlign: align,
          color: color,
          width: 'var(--cell-width)',
          maxWidth: 'var(--cell-width)'
        }}
      >
        <div
          className={styles.content({
            validation,
            // A small row shows one line: cut it short with an ellipsis, as the
            // header does, rather than wrapping into a second line that's clipped.
            class:
              rowHeight === rowHeights.Small && !selected
                ? 'truncate'
                : `break-words whitespace-pre-wrap ${selected ? 'overflow-y-auto' : 'overflow-y-hidden'}`
          })}
          style={{ maxHeight: `${getRowHeightVariable(rowHeight) - 2}px` }}
        >
          {typeof value === 'object' ? '' : value}
        </div>
      </div>
    )
  }
}

export const TextCell = TextCellComponent
